import React, { useState, useEffect } from 'react';
import { SelectPicker, TagGroup, Tag } from 'rsuite';
import { defaultLanguage, LanguageCode, languages, MultiLingual } from '@/models/language.model';
import Textarea from './Textarea';

interface MultilingualTextareaProps {
  value?: MultiLingual;
  onChange?: (value?: MultiLingual) => void;
  disabled?: boolean;
  rows?: number;
}

const MultilingualTextarea: React.FC<MultilingualTextareaProps> = ({value = {}, disabled, onChange, rows}) => {
  const [textareaValue, setTextareaValue] = useState('');
  const [selectedLang, setSelectedLang] = useState<LanguageCode>(defaultLanguage);

  useEffect(() => {
    // Update the TextArea value when the selected language changes
    setTextareaValue(value[selectedLang] || '');
  }, [selectedLang, value]);

  const handleTextareaChange = (newValue: string) => {
    setTextareaValue(newValue);

    if (!newValue.trim()) {
      // Remove the language from the value if TextArea is empty
      removeLanguage(selectedLang);
    } else {
      // Add or update the value for the selected language
      onChange && onChange({ ...value, [selectedLang]: newValue });
    }
  };
  
  const isEmptyObject = (obj: object): boolean => {
    return Object.keys(obj).length === 0;
  };
  const removeLanguage = (lang: LanguageCode) => {
    const { [lang]: _, ...updatedValue } = value;
    onChange && onChange(isEmptyObject(updatedValue) ? undefined : updatedValue);

    // Reset TextArea if the removed language is the currently selected one
    if (selectedLang === lang) {
      setTextareaValue('');
    }
  };

  // Inline styles for the component
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '8px',
      border: '1px solid #ddd',
      padding: '4px',
      borderRadius: '4px',
    },
    topRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: '#f7f7f7', // Soft blue background color
      padding: '4px',
      borderRadius: '4px 4px 0 0', // Rounded corners only on the top
      margin: '-4px -4px 0 -4px', // Negative margins to remove spacing
    },
    textarea: {
      width: '100%',
      resize: 'none' as const,
    },
    tagGroup: {
      display: 'flex',
      gap: '4px',
      flexWrap: 'wrap' as const,
    },
    tag:{
      backgroundColor: '#ffffff'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.topRow}>
        <SelectPicker
          size="xs"
          data={languages}
          value={selectedLang}
          onChange={val => setSelectedLang(val as LanguageCode)}
          searchable={false}
          cleanable={false}
          disabled={disabled}
        />
        <TagGroup style={styles.tagGroup}>
          {Object.keys(value).map(lang => (
            <Tag
              style={styles.tag}
              size='sm'
              key={lang}
              closable={!disabled}
              onClose={() => removeLanguage(lang as LanguageCode)}
              
            >
              {languages.findLast(l => l.value == lang)?.label ?? 'NaV'}
            </Tag>
          ))}
        </TagGroup>
      </div>
      <Textarea
        rows={rows}
        value={textareaValue}
        onChange={handleTextareaChange}
        placeholder="Enter text here..."
        style={styles.textarea}
        disabled={disabled}              
      />
    </div>
  );
};

export default MultilingualTextarea;
